import React from "react";
import {
  FaTools,
  FaSyncAlt,
  FaBrain,
  FaMobileAlt,
  FaShieldAlt,
  FaPhone,
} from "react-icons/fa";
import { useLanguage } from "../pages/LanguageContext";
import supportBg from "../images/Background.jpg";
import whyImage from "../images/Gemini_Generated_Image_scts4wscts4wscts.png";

const translations = {
  en: {
    title: "System Support & Maintenance",
    subtitle: "Ongoing reliability. Proactive service. Total peace of mind.",
    intro:
      "At Mass Smart Solutions, our job doesn’t end after installation. We stay with you, making sure your smart home remains as smooth, secure, and responsive as the day it was launched.",
    features: [
      {
        icon: <FaTools />,
        title: "Smart Troubleshooting",
        desc: "Our team of engineers and support specialists are ready to assist with any technical issue, whether remotely or on-site, ensuring fast and efficient resolution.",
      },
      {
        icon: <FaSyncAlt />,
        title: "Firmware & System Updates",
        desc: "We proactively maintain your smart devices by providing firmware upgrades and compatibility patches so your system stays current and secure.",
      },
      {
        icon: <FaBrain />,
        title: "AI-Powered Diagnostics",
        desc: "Early detection means fewer disruptions. Our systems notify both you and us of performance irregularities so we can act before problems escalate.",
      },
      {
        icon: <FaMobileAlt />,
        title: "Remote Monitoring & Health Checks",
        desc: "We regularly monitor the status of your system, ensure your network is healthy, and provide feedback or optimization suggestions whenever needed.",
      },
      {
        icon: <FaShieldAlt />,
        title: "Security Assurance",
        desc: "We make sure your surveillance, alarms, and smart locks remain operational, integrated, and secure with no blind spots or vulnerabilities.",
      },
    ],
    support: {
      heading: "Reliable Support You Can Count On",
      text: "All our clients benefit from 6 months of post-handover support. We offer remote help during holidays, and respond within 8 hours. After the period, on-demand or subscription-based service is available. All devices are backed by a 2-year warranty.",
    },
    why: {
      heading: "🎯 Why It Matters",
      bullets: [
        "Keep your investment protected and up to date",
        "Avoid costly disruptions and system errors",
        "Access expert help when you need it most",
        "Extend the life of your devices with proper care",
        "Feel confident your smart home is always working at its best",
        "Stay current with the latest trends in this rapidly evolving field",
        "Get help integrating new devices or appliances into your system",
      ],
    },
    cta: {
      title: "Let’s Keep Your Home Smart",
      desc: "Whether you need a routine check, emergency support, or want to enhance your system — we’re just a call away.",
      button: "Contact Us",
    },
  },

  ar: {
    title: "الدعم الفني والصيانة",
    subtitle: "موثوقية مستمرة، خدمة استباقية، وراحة بال تامة",
    intro:
      "في ماس سمارت سولوشنز، عملنا لا يتوقف عند مرحلة التركيب. نحن نرافقك بعد التسليم لضمان أن يبقى منزلك الذكي سلسًا وآمنًا وفعّالًا كما كان منذ اليوم الأول",
    features: [
      {
        icon: <FaTools />,
        title: "الدعم الفني الذكي",
        desc: "فريقنا من المهندسين والمتخصصين في الدعم جاهز لمساعدتك في أي مشكلة تقنية سواء عن بُعد أو من خلال زيارة ميدانية، لضمان الحل السريع والفعّال",
      },
      {
        icon: <FaSyncAlt />,
        title: "تحديثات النظام والبرمجيات",
        desc: "نقوم بصيانة نظامك بانتظام عبر تحديثات البرامج الثابتة وضمان التوافق مع التقنيات الجديدة حتى يبقى منزلك الذكي محدثًا وآمنًا",
      },
      {
        icon: <FaBrain />,
        title: "تشخيص ذكي مدعوم بالذكاء الاصطناعي",
        desc: "نرصد أي خلل محتمل في الأداء ونخطرك مباشرة به، مما يسمح بالتدخل المبكر قبل أن تتفاقم المشكلة",
      },
      {
        icon: <FaMobileAlt />,
        title: "مراقبة دورية وفحص صحة النظام",
        desc: "نراقب حالة نظامك ونتحقق من كفاءة الشبكة ونقدّم توصيات دورية للتحسين عند الحاجة",
      },
      {
        icon: <FaShieldAlt />,
        title: "تأمين الأنظمة الذكية",
        desc: "نضمن استمرار عمل الكاميرات، أجهزة الإنذار، والأقفال الذكية بكفاءة وتكامل تام دون أي ثغرات أو نقاط عمياء",
      },
    ],
    support: {
      heading: "دعم موثوق لجميع عملائنا",
      text: "يستفيد جميع العملاء من ستة أشهر من الدعم الفني المجاني، مع دعم خلال العطل واستجابة خلال 8 ساعات. بعد الفترة، تتوفر خدمة حسب الطلب أو من خلال اشتراك. جميع الأجهزة مضمونة لمدة عامين",
    },
    why: {
      heading: "🎯 لماذا هذا مهم",
      bullets: [
        "حماية استثمارك وضمان تحديثه باستمرار",
        "تجنّب الأعطال المفاجئة والمكلفة",
        "الوصول السريع إلى الدعم عند الحاجة",
        "إطالة عمر الأجهزة من خلال الرعاية المناسبة",
        "راحة بال بأن نظامك الذكي يعمل بكفاءة طوال الوقت",
        "متابعة أحدث الاتجاهات والتقنيات في هذا المجال سريع التطور",
        "المساعدة في دمج الأجهزة الذكية الجديدة مع النظام الحالي",
      ],
    },
    cta: {
      title: "اجعل منزلك الذكي يعمل كما يجب",
      desc: "سواء كنت بحاجة إلى فحص دوري، دعم طارئ، أو تطوير نظامك — نحن هنا لخدمتك",
      button: "اتصل بنا",
    },
  },
};

const SmartSecurityPage = () => {
  const languageContext = useLanguage();
  const language = languageContext?.language || "en";
  const t =
    translations[language] ||
    translations["en"] ||
    Object.values(translations)[0];
  if (!t)
    return (
      <div style={{ color: "red", textAlign: "center", margin: 50 }}>
        Translation Error: No content available.
      </div>
    );
  const isAr = language === "ar";

  return (
    <div
      style={{
        direction: isAr ? "rtl" : "ltr",
        fontFamily: isAr ? "Cairo, sans-serif" : "Poppins, sans-serif",
        background: "linear-gradient(120deg, #f1f5f9 60%, #e0f4ff 100%)",
        overflow: "hidden",
        minHeight: "100vh",
      }}
      className="system-support-root"
    >
      <style>{`
        .blue-feature-card {
          background: rgba(37,99,235,0.09);
          border-radius: 2.3rem;
          box-shadow: 0 2px 13px 0 rgba(37,99,235,0.07), 0 1.5px 7px rgba(37,99,235,0.05);
          border: 1.5px solid #2297e61a;
          display: flex;
          flex-direction: column;
          align-items: center;
          transition: transform .27s cubic-bezier(.4,2,.2,1), box-shadow .22s cubic-bezier(.4,2,.2,1);
          padding: 2.3rem 1.3rem 2.1rem;
          position: relative;
          min-height: 310px;
          outline: none;
          overflow: hidden;
        }
        .blue-feature-card:hover {
          transform: translateY(-7px) scale(1.03);
          box-shadow: 0 16px 38px #2297e61b;
          border-color: #2297e640;
        }
        .blue-feature-icon-outer {
          background: #fff;
          border: 2px solid #2297e660;
          width: 70px; height: 70px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 50%;
          margin-bottom: 1.1rem;
          transition: border-color .33s cubic-bezier(.4,2,.2,1);
          box-shadow: 0 1.5px 8px #2297e61c;
        }
        .blue-feature-card:hover .blue-feature-icon-outer {
          border-color: #1b4fbdff;
        }
        .blue-feature-title {
          color: #1b4fbdff;
          font-size: 1.14rem;
          font-weight: 700;
          margin-bottom: .4rem;
          margin-top: 0;
          text-align: center;
          letter-spacing: -.01em;
        }
        .blue-feature-underline {
          display: block;
          width: 2.2rem;
          height: 3px;
          border-radius: 2px;
          background: linear-gradient(90deg,#2297e6 40%,#3fd4ff 100%);
          margin: 0.2rem auto 1.05rem;
        }
        .blue-feature-desc {
          background: rgba(237,245,255,0.91);
          border-radius: 0.8rem;
          padding: 1rem 0.8rem 0.7rem;
          color: #2a4058;
          font-size: 0.99rem;
          line-height: 1.66;
          font-weight: 500;
          box-shadow: 0 1px 7px #2297e611;
          text-align: center;
          margin: 0;
        }
        @media (max-width: 1150px) {
          .feature-grid { grid-template-columns: repeat(2,1fr) !important; }
        }
        @media (max-width: 800px) {
          .feature-grid { grid-template-columns: 1fr !important; }
        }
        /* --- WHY CARD FLEX --- */
        .why-card-flex {
          display: flex;
          gap: 3.4rem;
          align-items: stretch;
          flex-direction: row;
          background: linear-gradient(110deg, #e0eafd 70%, #f8fbff 100%);
          border-radius: 2.7rem;
          box-shadow: 0 8px 32px 0 rgba(34,151,230,0.13), 0 2px 10px rgba(34,151,230,0.08);
          border: 2px solid #2297e626;
          overflow: visible;
          position: relative;
        }
        .why-card-flex:before {
          content: "";
          position: absolute;
          inset: 0;
          z-index: 0;
          background: radial-gradient(circle at 70% 70%, #90e0ff38 0%, #fff0 70%);
          border-radius: 2.7rem;
          pointer-events: none;
        }
        .why-card-flex .why-card-text {
          position: relative;
          z-index: 2;
        }
        .why-card-flex .why-card-img-outer {
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 0;
          flex: 1.5;
          position: relative;
        }
        .why-card-flex .why-card-img-inner {
          position: relative;
          background: linear-gradient(130deg, #1b4fbdff 0%, #5ad7fb 100%);
          border-radius: 2.2rem;
          box-shadow: 0 18px 38px #2297e63d, 0 8px 18px #2297e63d;
          padding: .44rem;
          max-width: 370px;
          margin: 0 auto;
          transition: box-shadow 0.19s;
        }
        .why-card-flex .why-card-img-inner:hover {
          box-shadow: 0 32px 62px #2297e67a, 0 14px 42px #2297e637;
        }
        .why-card-flex .why-card-img-inner img {
          display: block;
          width: 340px;
          max-width: 88vw;
          border-radius: 1.65rem;
          box-shadow: 0 2px 15px #2297e62b, 0 1px 8px #2297e619;
          object-fit: cover;
        }
        @media (max-width: 1050px) {
          .why-card-flex { gap: 1.7rem; }
          .why-card-flex .why-card-img-inner img { width: 260px; }
        }
        @media (max-width: 850px) {
          .why-card-flex {
            flex-direction: column !important;
            gap: 2.2rem !important;
            padding: 2.2rem 1rem !important;
            text-align: center !important;
            align-items: center !important;
          }
          .why-card-flex .why-card-img-inner img {
            margin: 0 auto !important;
            width: 95vw;
            min-width: 0;
            max-width: 330px;
          }
        }
        /* --- DARK MODE --- */
        .dark .system-support-root { background: #111 !important; }
        .dark .blue-feature-card {
          background: #182143 !important;
          color: #fff !important;
          border: 1.5px solid #1b4fbdff !important;
          box-shadow: 0 10px 32px #2297e62a !important;
        }
        .dark .blue-feature-icon-outer {
          background: #111 !important;
          border: 2px solid #1b4fbdff !important;
          box-shadow: 0 1.5px 8px #2297e64a !important;
        }
        .dark .blue-feature-title {
          color: #1b4fbdff !important;
        }
        .dark .blue-feature-underline {
          background: linear-gradient(90deg,#2297e6 40%,#3fd4ff 100%) !important;
        }
        .dark .blue-feature-desc {
          background: #171f37 !important;
          color: #dde8ff !important;
          box-shadow: 0 2px 10px #2297e62c !important;
        }
        .dark .support-info-card {
          background: #171f37 !important;
          color: #dde8ff !important;
          border: 1.5px solid #1b4fbdff !important;
          box-shadow: 0 8px 32px #2297e62a !important;
        }
        .dark .support-info-card h2 {
          color: #1b4fbdff !important;
        }
        .dark .support-info-card p {
          color: #dde8ff !important;
        }
        .dark .why-card-flex {
          background: #1e2439 !important;
          color: #dde8ff !important;
          border: 2px solid #2297e655 !important;
        }
        .dark .why-card-flex h2 {
          color: #1b4fbdff !important;
        }
        .dark .why-card-flex .why-card-img-inner {
          background: linear-gradient(130deg,#2297e6 10%,#38bdf8 90%);
        }
        .dark .why-card-flex .why-card-img-inner img {
          box-shadow: 0 2px 19px #2297e641, 0 1px 8px #2297e621;
        }
        .dark .cta-card {
           background: linear-gradient(115deg, #000000ff 12%, #626a7cff 110%) !important;
          
        }
        .dark .cta-card h2 {
          color: #1b4fbdff !important;
        }
        .dark .cta-card p {
          color: #dde8ff !important;
        }
        .dark .cta-card a {
           background: linear-gradient(96deg, #02101e 0%, #145a9b 100%) !important;
  color: #eaf6ff !important;
        }
      `}</style>

      <div
        style={{
          background: `linear-gradient(to right,rgba(31,41,55,0.69),rgba(34,151,230,0.18)), url(${supportBg}) center/cover`,
          color: "#fff",
          textAlign: "center",
          padding: "6rem 1rem 4rem",
          position: "relative",
          zIndex: 2,
        }}
      >
        <h1
          style={{
            fontSize: isAr ? "2.4rem" : "3rem",
            fontWeight: "900",
            marginBottom: "1.1rem",
            letterSpacing: "-0.02em",
            textShadow: "0 4px 20px #232d4152",
          }}
        >
          {t.title}
        </h1>
        <p
          style={{
            fontSize: isAr ? "1.15rem" : "1.22rem",
            maxWidth: "730px",
            margin: "0 auto",
            lineHeight: 1.9,
            fontWeight: 500,
            letterSpacing: "0.01em",
          }}
        >
          {t.subtitle}
        </p>
        <p
          style={{
            fontSize: isAr ? "1.14rem" : "1.17rem",
            maxWidth: "750px",
            margin: "1.2rem auto 0",
            lineHeight: 1.95,
            fontWeight: 500,
            color: "#363f48ff",
            textShadow: "0 2px 16px #232d4155",
          }}
        >
          {t.intro}
        </p>
      </div>

      <div
        style={{
          marginTop: "-1rem",
          padding: "6rem 2rem 3.5rem 2rem",
          boxSizing: "border-box",
        }}
      >
        <div
          className="feature-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2.5, 1fr)",
            gap: "2.2rem",
            padding: "0 0 2.7rem 0",
            zIndex: 3,
            position: "relative",
            maxWidth: 900,
            margin: "0 auto",
          }}
        >
          {t.features.map((item, idx) => (
            <div className="blue-feature-card" key={idx} tabIndex={0}>
              <div className="blue-feature-icon-outer">
                <span style={{ fontSize: 34, color: "#1b4fbdff" }}>
                  {item.icon}
                </span>
              </div>
              <h3 className="blue-feature-title">{item.title}</h3>
              <span className="blue-feature-underline" />
              <p className="blue-feature-desc">{item.desc}</p>
            </div>
          ))}
        </div>
        <div
          className="support-info-card"
          style={{
            maxWidth: "1070px",
            margin: "auto",
            textAlign: "center",
            background: "rgba(247,250,255,0.98)",
            padding: isAr ? "2.2rem 1.5rem 2.8rem" : "3.2rem",
            borderRadius: "2.3rem",
            boxShadow: "0 14px 36px rgba(59,130,246,0.10)",
            border: "1px solid #e2e8f0",
          }}
        >
          <h2
            style={{
              color: "#1b4fbdff",
              fontSize: "1.63rem",
              fontWeight: "bold",
              marginBottom: "1.3rem",
            }}
          >
            {t.support.heading}
          </h2>
          <p
            style={{
              fontSize: "1.13rem",
              color: "#1e293b",
              lineHeight: 1.87,
              fontWeight: 500,
            }}
          >
            {t.support.text}
          </p>
        </div>
      </div>

      <div
        className="why-card-flex"
        style={{
          maxWidth: 1100,
          margin: "3.5rem auto 3.7rem",
          padding: isAr
            ? "2.6rem 2rem 2.3rem 1.3rem"
            : "2.6rem 1.3rem 2.3rem 2rem",
          position: "relative",
          zIndex: 5,
          backdropFilter: "blur(7px)",
        }}
      >
        <div className="why-card-text" style={{ flex: 2.2, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",

              marginBottom: "1.35rem",
            }}
          >
            <h2
              style={{
                fontSize: "1.43rem",
                fontWeight: "bold",
                color: "#1b4fbdff",
                letterSpacing: "-.01em",
                margin: 0,
                display: "flex",
                alignItems: "center",
                gap: "0.7rem",
              }}
            >
              <span style={{ fontSize: "1.5rem", marginTop: "-2px" }}></span>{" "}
              {t.why.heading}
            </h2>
          </div>
          <ul
            style={{
              padding: 0,
              margin: 0,
              listStyle: "none",
              textAlign: isAr ? "right" : "left",
              direction: isAr ? "rtl" : "ltr",
            }}
          >
            {t.why.bullets.map((point, i) => (
              <li
                key={i}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: ".85rem",
                  fontSize: isAr ? "1.2rem" : "1.2rem",
                  lineHeight: 1.83,
                  fontWeight: 500,

                  marginBottom: ".65rem",
                }}
              >
                <span
                  style={{
                    width: 25,
                    height: 25,
                    minWidth: 25,
                    minHeight: 25,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                      "linear-gradient(135deg,#2297e6 60%,#38bdf8 100%)",
                    borderRadius: "50%",
                    boxShadow: "0 2px 8px #2297e624",
                    marginTop: "3px",
                  }}
                >
                  <svg width="15" height="13" viewBox="0 0 15 13" fill="none">
                    <path
                      d="M2 7.2L6 11L13 2"
                      stroke="#fff"
                      strokeWidth="2.1"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="why-card-img-outer">
          <div className="why-card-img-inner">
            <img
              src={whyImage}
              alt={isAr ? "أهمية الدعم" : "Why Support Matters"}
            />
          </div>
        </div>
      </div>

      <div
        className="cta-card"
        style={{
          background:
            "radial-gradient(circle at 60% 0%, #1a3070 12%, #59b8fd 110%)",
          padding: "4.1rem 2vw 4.6rem",
          textAlign: "center",
          borderRadius: "2.2rem",
          margin: "0 auto 0",
          position: "relative",
          zIndex: 3,
        }}
      >
        <h2
          style={{
            fontSize: "1.68rem",
            fontWeight: "bold",
            color: "#ffffffff",
            marginBottom: "1.1rem",
            letterSpacing: "-0.01em",
          }}
        >
          {t.cta.title}
        </h2>
        <p
          style={{
            fontSize: "1.05rem",
            color: "#ffffffff",
            marginBottom: "2rem",
            fontWeight: 500,
          }}
        >
          {t.cta.desc}
        </p>
        <a
          href="/contact"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
            background: "linear-gradient(96deg,#ffe6cf 0%,#f2ecdf 100%)",
            color: "#164196",
            padding: "1.04rem 2.4rem",
            borderRadius: "999px",
            fontWeight: "bold",
            fontSize: "1.1rem",
            textDecoration: "none",
            boxShadow: "0 8px 18px #2297e62b,0 2px 7px #2297e62b",
            transition: "transform 0.2s, box-shadow .22s",
            border: "none",
            outline: "none",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.06)";
            e.currentTarget.style.boxShadow =
              "0 14px 36px #2297e63b,0 4px 13px #2297e62a";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow =
              "0 8px 18px #2297e62b,0 2px 7px #2297e62b";
          }}
        >
          <FaPhone /> {t.cta.button}
        </a>
      </div>
    </div>
  );
};

export default SmartSecurityPage;
